// pkg166 — party-name guard surfaced: red banner + option marker for documents whose question body names a party (body_check from the list endpoint); page copy now states the intro, verification, and RFP heading are rebuilt too and that names in the questions are rejected at upload — delivered 2026-08-30
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { adminApi } from '../utils/adminApi';

// Standard Discovery — Tool A (upload-and-fill).
//
// One simple flow: pick a practice area, pick a case type, upload that case
// type's standard discovery document. The app reads it once and turns the
// case-specific details (parties, court, docket, opposing counsel, signature)
// into fields it fills automatically, while your interrogatories and requests
// stay exactly as written. When you generate discovery for a client, the
// document for that client's case type is used.

const META = 'Arial, sans-serif';

export default function AdminDiscoveryPage() {
  const [practiceAreas, setPracticeAreas] = useState([]);
  const [caseTypes, setCaseTypes] = useState([]);
  const [docs, setDocs] = useState({});        // case_type_id -> { doc_filename, fields, auto_filled }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [areaKey, setAreaKey] = useState('');
  const [caseTypeId, setCaseTypeId] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const [showReassign, setShowReassign] = useState(false);
  const [reAreaKey, setReAreaKey] = useState('');
  const [reCaseTypeId, setReCaseTypeId] = useState('');

  const flash = useCallback((msg) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 4000);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [pa, cts, docList] = await Promise.all([
          adminApi.getFirmPracticeAreas().catch(() => ({ practice_areas: [] })),
          adminApi.listCaseTypes().catch(() => []),
          adminApi.listDiscoveryDocuments().catch(() => ({ documents: [] })),
        ]);
        if (cancelled) return;
        setPracticeAreas(pa.practice_areas || []);
        setCaseTypes(Array.isArray(cts) ? cts : (cts.case_types || []));
        const map = {};
        (docList.documents || []).forEach((d) => { map[d.case_type_id] = d; });
        setDocs(map);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const areaCaseTypes = caseTypes
    .filter((ct) => (ct.practice_area || '') === areaKey)
    .slice()
    .sort((a, b) => (a.label || '').localeCompare(b.label || ''));

  const selectedDoc = caseTypeId ? (docs[caseTypeId] || null) : null;
  const hasDoc = !!selectedDoc;
  // pkg166: a stored document that the party-name guard now refuses (uploaded
  // before the guard existed). Generate will refuse it until it is replaced.
  const docNeedsAttention = (d) => !!(d && d.body_check && d.body_check.ok === false);
  const selectedNeedsAttention = docNeedsAttention(selectedDoc);

  function onAreaChange(e) {
    setAreaKey(e.target.value);
    setCaseTypeId('');           // reset case type when area changes
    setError('');
    closeReassign();
  }

  async function doUpload(file) {
    if (!file || !caseTypeId) return;
    setBusy(true); setError('');
    try {
      const r = await adminApi.uploadDiscoveryDocByCaseType(caseTypeId, file);
      // pkg166: an accepted upload has passed the party-name guard, so it is clean.
      setDocs((prev) => ({ ...prev, [caseTypeId]: { doc_filename: r.doc_filename, fields: r.fields || [], auto_filled: r.auto_filled || [], body_check: { ok: true } } }));
      flash('Uploaded. Your questions are kept as-is; the caption, introduction, verification, signature, and certificate are rebuilt for each case at generate time.');
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function removeDoc() {
    if (!window.confirm('Remove the discovery document for this case type?')) return;
    setError('');
    try {
      await adminApi.deleteDiscoveryDocByCaseType(caseTypeId);
      setDocs((prev) => { const n = { ...prev }; delete n[caseTypeId]; return n; });
      flash('Document removed.');
    } catch (err) {
      setError(err.message || 'Could not remove.');
    }
  }

  function closeReassign() { setShowReassign(false); setReAreaKey(''); setReCaseTypeId(''); }

  async function downloadDoc() {
    setError('');
    try {
      await adminApi.downloadDiscoveryDocByCaseType(caseTypeId, selectedDoc && selectedDoc.doc_filename);
    } catch (err) {
      setError(err.message || 'Could not download.');
    }
  }

  async function doReassign() {
    if (!reCaseTypeId) { setError('Pick a case type to move to.'); return; }
    setBusy(true); setError('');
    try {
      await adminApi.reassignDiscoveryDoc(caseTypeId, reCaseTypeId);
      const moved = docs[caseTypeId];
      setDocs((prev) => { const n = { ...prev }; delete n[caseTypeId]; if (moved) n[reCaseTypeId] = moved; return n; });
      const target = caseTypes.find((c) => c.id === reCaseTypeId);
      closeReassign();
      if (target) { setAreaKey(target.practice_area || ''); setCaseTypeId(reCaseTypeId); }
      flash('Document moved.');
    } catch (err) {
      setError(err.message || 'Could not move the document.');
    } finally {
      setBusy(false);
    }
  }

  function selectCaseType(id) { setCaseTypeId(id); setError(''); closeReassign(); }

  const selectStyle = {
    fontFamily: META, fontSize: 14, padding: '8px 10px', borderRadius: 4,
    border: '1px solid var(--border)', background: 'var(--white)', color: 'var(--dark)',
    width: '100%', maxWidth: 420,
  };

  return (
    <div>
      <h1 className="page-title">Discovery Templates</h1>
      <p className="page-subtitle">
        Pick a practice area and case type, then upload that case type&apos;s standard discovery
        document (the Word file you already use). Your interrogatories and requests are kept exactly
        as written — the app rebuilds the caption, title, &ldquo;To:&rdquo; block, introduction,
        verification, request-for-production heading, signature, and certificate of service
        automatically when you generate, using your firm settings and the case details you enter on
        the client. Because the questions themselves are reused on every case, a document that names
        a party anywhere in its questions is rejected at upload. When you generate discovery for a
        client, the document for that client&apos;s case type is used. Only owners can edit this.
      </p>

      {error   && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {loading && <div className="card" style={{ fontFamily: META, color: 'var(--light)' }}>Loading…</div>}

      {!loading && (
        <div className="card" style={{ fontFamily: META, maxWidth: 560 }}>
          <label style={{ display: 'block', fontSize: 13, color: 'var(--dark)', marginBottom: 6 }}>Practice area</label>
          <select value={areaKey} onChange={onAreaChange} style={selectStyle}>
            <option value="">Select a practice area…</option>
            {practiceAreas
              .slice()
              .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
              .map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>

          {areaKey && (
            <div style={{ marginTop: 16 }}>
              <label style={{ display: 'block', fontSize: 13, color: 'var(--dark)', marginBottom: 6 }}>Case type</label>
              {areaCaseTypes.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--light)' }}>No case types in this practice area yet.</div>
              ) : (
                <select value={caseTypeId} onChange={(e) => selectCaseType(e.target.value)} style={selectStyle}>
                  <option value="">Select a case type…</option>
                  {areaCaseTypes.map((ct) => (
                    <option key={ct.id} value={ct.id}>
                      {ct.label}{docs[ct.id] ? (docNeedsAttention(docs[ct.id]) ? '  ⚠ needs attention' : '  ✓ document uploaded') : ''}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {caseTypeId && (
            <div style={{ marginTop: 18, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
              {hasDoc ? (
                <>
                  <div style={{ fontSize: 14, color: 'var(--dark)', marginBottom: 10 }}>
                    {selectedNeedsAttention ? '⚠' : '✓'} {selectedDoc.doc_filename || 'Document uploaded'}
                  </div>
                  {selectedNeedsAttention && (
                    <div role="alert" style={{ background: '#fdf1ef', border: '1px solid #e2b9b4', borderRadius: 4, padding: '10px 12px', marginBottom: 14, fontSize: 13, color: '#7a1f15', lineHeight: 1.5 }}>
                      <strong>This document will not generate until it is replaced.</strong>{' '}
                      {selectedDoc.body_check.message || 'It could not be checked.'}
                      {' '}Use <strong>Download</strong> to get the stored copy, fix it in Word, then <strong>Replace document</strong>.
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
                    <button className="btn btn-sm" disabled={busy} onClick={downloadDoc}>
                      Download
                    </button>
                    <button className="btn btn-sm" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}>
                      {busy ? 'Uploading…' : 'Replace document'}
                    </button>
                    <button className="btn btn-sm" disabled={busy} onClick={() => (showReassign ? closeReassign() : setShowReassign(true))}>
                      Reassign
                    </button>
                    <button className="btn btn-sm" disabled={busy} onClick={removeDoc} style={{ color: '#b0261a', borderColor: '#e2b9b4' }}>
                      Remove
                    </button>
                  </div>

                  <div style={{ fontSize: 12, color: 'var(--light)', marginBottom: 14, lineHeight: 1.5 }}>
                    The app keeps your interrogatories and requests exactly as written. Each time you
                    generate, it builds a fresh caption, title, &ldquo;To:&rdquo; block, introduction,
                    verification, request-for-production heading, signature, and certificate of service
                    from your firm settings and the case details (parties, court, docket, division,
                    opposing counsel, and which side you represent) — so there is nothing to blank out or
                    hand-edit here. Party names are never allowed inside the questions themselves. To swap
                    in a revised version of your questions, use <strong> Replace document</strong>.
                  </div>

                  {showReassign && (
                    <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 4, padding: 12, marginBottom: 14 }}>
                      <div style={{ fontSize: 12, color: 'var(--light)', marginBottom: 8 }}>Move this document to another practice area and case type:</div>
                      <select value={reAreaKey} onChange={(e) => { setReAreaKey(e.target.value); setReCaseTypeId(''); }} style={{ ...selectStyle, marginBottom: 8 }}>
                        <option value="">Practice area…</option>
                        {practiceAreas
                          .slice()
                          .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
                          .map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                      </select>
                      {reAreaKey && (
                        <select value={reCaseTypeId} onChange={(e) => setReCaseTypeId(e.target.value)} style={{ ...selectStyle, marginBottom: 10 }}>
                          <option value="">Case type…</option>
                          {caseTypes
                            .filter((ct) => (ct.practice_area || '') === reAreaKey && ct.id !== caseTypeId)
                            .slice()
                            .sort((a, b) => (a.label || '').localeCompare(b.label || ''))
                            .map((ct) => (
                              <option key={ct.id} value={ct.id}>
                                {ct.label}{docs[ct.id] ? '  (already has a document)' : ''}
                              </option>
                            ))}
                        </select>
                      )}
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="btn btn-sm" disabled={busy || !reCaseTypeId} onClick={doReassign}>
                          {busy ? 'Moving…' : 'Move here'}
                        </button>
                        <button className="btn btn-sm" disabled={busy} onClick={closeReassign}>Cancel</button>
                      </div>
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: 'var(--light)', marginBottom: 4 }}>
                    Generated automatically each time: caption, title, &ldquo;To:&rdquo; block, introduction,
                    verification, request-for-production heading, signature, and certificate of service.
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, color: 'var(--light)', marginBottom: 12 }}>
                    No standard discovery document for this case type yet.
                  </div>
                  <button className="btn" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}>
                    {busy ? 'Uploading…' : 'Upload document'}
                  </button>
                </>
              )}
            </div>
          )}

          <input
            ref={fileRef}
            type="file"
            accept=".docx"
            style={{ display: 'none' }}
            onChange={(e) => doUpload(e.target.files && e.target.files[0])}
          />
        </div>
      )}
    </div>
  );
}
